// src/lib/useApi.ts
//
// One small hook so every page renders the same loading / error shape instead
// of five slightly different copies of the same `useState` triple.
import { useCallback, useEffect, useState } from 'react'
import { ApiError, isNetworkError } from './api'
import { isRetryable, NETWORK_FAILURE } from './messages'

export interface ApiFetchState<T> {
  readonly data: T | undefined
  readonly error: string | null
  /** A request is in flight. Use this for `aria-busy`, never to unmount content. */
  readonly loading: boolean
  /**
   * A request is in flight and there is nothing on screen yet.
   *
   * ⚠ The distinction exists because of a real keyboard defect. Every sortable
   * table swapped itself for a skeleton on `loading`, so clicking a column
   * header unmounted the very `<button>` that was clicked — mid-request, while
   * it still held focus. The browser has nowhere to put focus when its owner
   * disappears, so it fell to `<body>`: a keyboard user pressed Enter on
   * "Amount" and landed back at the top of the document with no way to reach
   * the next header except re-tabbing the whole page.
   *
   * A REFRESH keeps the previous rows mounted (and marks the region
   * `aria-busy`), so the header survives the round trip and focus never moves.
   * Only a FIRST load — nothing rendered yet, nothing to keep — shows the
   * skeleton.
   */
  readonly initialLoading: boolean
  readonly reload: () => void
  /**
   * False when a retry cannot change the answer (a refusal, or a record that does not exist), so a screen shows no Retry
   * button (UAT re-run F-29, F-30). True for a dropped connection or a server fault, and while there is no error.
   */
  readonly retryable: boolean
  /** The HTTP status of the failure, or null for none or for a request that got no answer. */
  readonly errorStatus: number | null
}

export function useApi<T>(fetcher: () => Promise<T>, deps: readonly unknown[]): ApiFetchState<T> {
  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [errorStatus, setErrorStatus] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setErrorStatus(null)
    fetcher()
      .then((result) => {
        if (!cancelled) {
          setData(result)
          setLoading(false)
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setErrorStatus(err instanceof ApiError ? err.status : null)
        setError(
          isNetworkError(err)
            ? NETWORK_FAILURE
            : err instanceof Error
              ? err.message
              : 'An unknown error occurred.',
        )
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return {
    data,
    error,
    loading,
    initialLoading: loading && data === undefined,
    reload,
    retryable: error === null || isRetryable(errorStatus),
    errorStatus,
  }
}
